import React, { useState } from "react";
import axios from "axios";
import { Button, OutlineButton } from "components/button";
import Field from "components/field";
import Select from "react-select";
import { useModalContext } from "components/modal";
import ClientSelect from "components/react-select/clients";
import TaxSelectSettings from "components/react-select/taxes-settings";
import { useSnackbarContext } from "components/snackbar";
import { useFormik } from "formik";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { TCustomer } from "types/customer";
import { TProperty, TPropertyForm } from "types/property";
import * as Yup from "yup";
import { NewPropertyMapContainer } from "./NewPropertyMapContainer";
import { TTag } from "types/tag";
import Sort from "components/sort";
import { useTranslation } from 'react-i18next';

type Props = {
  customerId?: string;
  property?: TProperty;
  customerInfo?: TCustomer;
  notModal?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  returnedPropertyId?: (property: TProperty) => void;
};
export default function NewPropertyModal(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.properties.newPropertyModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const modal = useModalContext();
  const snackbar = useSnackbarContext();
  const queryClient = useQueryClient();

  const tagsQuery = useQuery<Array<TTag>>("tags", async () =>
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tags/customer&property`)
      .then((res) => res.data)
  );

  const tagOptions = React.useMemo(() => {
    return tagsQuery.data?.map((tag) => ({ value: tag.id, label: tag.label })) || [];
  }, [tagsQuery.data]);
  

  const [sortedTags, setSortedTags] = useState(tagOptions);

  const mutation = useMutation(
    async (e: TPropertyForm) =>
      await axios
        .post(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/property/${props.property?.id}
    `,
          {
            ...e,
            address: addressObj.address,
            isStandardizedAddress: addressObj.isStandardizedAddress,
          }
        )
        .then((res) => {props.returnedPropertyId && props.returnedPropertyId(res.data); return res.data}),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries();
        !props.notModal && modal.setModal();
        snackbar.showSnackbar(
          `${tr("Property")} ${props.property ? tr("modified!") : tr("created!")}`
        );
        props.onSubmit && props.onSubmit();
        props.returnedPropertyId && props.returnedPropertyId(res);
      },
    }
  );

  const initialValues = {
    address: props.property?.address || "",
    city: props.property?.city || "",
    state: props.property?.state || "",
    name: props.property?.name || "",
    zipcode: props.property?.zipcode || "",
    zip_ext: props.property?.zip_ext || "",
    owner: props.property?.owner
      ? { ...props.property.owner, id: props.property.owner.id.toString() }
      : props.customerInfo || null,
    billed_to: props.property?.billed_to
      ? {
          ...props.property.billed_to,
          id: props.property.billed_to.id.toString(),
        }
      : props.customerInfo || null,
    default_tax: props.property?.default_tax || null,
    default_tax_group: (props.property as any)?.default_tax_group || null,
    latitude: props.property?.latitude || null,
    longitude: props.property?.longitude || null,
    tags: props.property?.tags?.map(tag => ({
      value: tag.id,
      label: tag.label,
    })) || [],
  };

  const validationSchema = Yup.object({
    address: Yup.string().required(),
    city: Yup.string().required(),
    state: Yup.string().required().length(2),
    zipcode: Yup.string().required().length(5),
    zip_ext: Yup.string().nullable().length(4),
    name: Yup.string(),
    owner: Yup.object().nullable(),
    billed_to: Yup.object().nullable(),
    latitude: Yup.number().nullable(),
    longitude: Yup.number().nullable(),
    tags: Yup.array().nullable(),
  });
  
  const onSubmit = (e: TPropertyForm) => {
    const tags = e.tags ? e.tags.map((tag) => tag.value) : [];
    mutation.mutate({ ...e, tags });
  };

  const formik = useFormik<TPropertyForm>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  const [addressObj, setAddressObj] = React.useState({
    address: formik.values.address || "",
    isStandardizedAddress: false,
    selectedOption: {
      description: formik.values.address || "",
      city: formik.values.city || "",
      state: formik.values.state || "",
      zipcode: formik.values.zipcode || "",
      zip_ext: formik.values.zip_ext || "",
    },
  });

  React.useEffect(() => {
    formik.setValues((v) => ({
      ...v,
      address: addressObj.address,
      // address: addressObj.selectedOption.description,
      city: addressObj.selectedOption.city,
      state: addressObj.selectedOption.state,
      zipcode: addressObj.selectedOption.zipcode,
      zip_ext: addressObj.selectedOption.zip_ext,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressObj]);

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4 max-w-[800px]">
      {/* Address Input */}
      {/* <StandardizedAddressInput {...{ setAddressObj, addressObj }} label={tr("Address")} formik={formik} required /> */}
      <Field
        name="address"
        required
        label={tr("Address")}
        value={addressObj.address || ""}
        onChange={(e: any) =>
          setAddressObj({
            ...addressObj,
            isStandardizedAddress: false,
            selectedOption: { ...addressObj.selectedOption, description: e.target.value },
            address: e.target.value,
          })
        }
        error={formik.touched.address && formik.errors.address ? String(formik.errors.address) : ""}
      />
  
      {/* City, State, Zipcode */}
      <div className="flex space-x-2">
        <Field
          name="city"
          className="w-[35%]"
          required
          label={tr("City")}
          value={addressObj.selectedOption.city || ""}
          onChange={(e: any) =>
            setAddressObj({
              ...addressObj,
              isStandardizedAddress: false,
              selectedOption: { ...addressObj.selectedOption, city: e.target.value },
            })
          }
          error={formik.touched.city && formik.errors.city ? String(formik.errors.city) : ""}
        />
        <Field
          name="state"
          className="w-[20%]"
          required
          label={tr("State")}
          value={addressObj.selectedOption.state || ""}
          onChange={(e: any) =>
            setAddressObj({
              ...addressObj,
              isStandardizedAddress: false,
              selectedOption: { ...addressObj.selectedOption, state: e.target.value },
            })
          }
          error={formik.touched.state && formik.errors.state ? String(formik.errors.state) : ""}
        />

          <Field
            name="zipcode"
            className="w-[20%]"
            required
            label={tr("Zipcode")}
            value={addressObj.selectedOption.zipcode || ""}
            onChange={(e: any) =>
              setAddressObj({
                ...addressObj,
                isStandardizedAddress: false,
                selectedOption: { ...addressObj.selectedOption, zipcode: e.target.value },
              })
            }
            maxLength={5}
            error={formik.touched.zipcode && formik.errors.zipcode ? String(formik.errors.zipcode) : ""}
          />
          <div className="bg-black h-[1px] w-[1%] mt-[42px]" />
          <Field
            name="zip_ext"
            className="w-[15%]"
            label="Ext"
            value={addressObj.selectedOption.zip_ext || ""}
            onChange={(e: any) =>
              setAddressObj({
                ...addressObj,
                isStandardizedAddress: false,
                selectedOption: { ...addressObj.selectedOption, zip_ext: e.target.value },
              })
            }
            maxLength={4}
            error={formik.touched.zip_ext && formik.errors.zip_ext ? String(formik.errors.zip_ext) : ""}
          />

      </div>
  
      {/* Nickname */}
      <Field name="name" formik={formik} label={tr("Nickname")} />
  
      {/* Owner and Billed To */}
      <div className="flex space-x-2">
        <ClientSelect
          error={formik.touched.owner && formik.errors.owner ? String(formik.errors.owner) : ""}
          isClearable
          value={
            formik.values.owner
              ? { ...formik.values.owner, value: formik.values.owner.id, label: formik.values.owner.name }
              : null
          }
          label={tr("Owner")}
          onChange={(e) => formik.setValues((v) => ({ ...v, owner: e }))}
        />
        <ClientSelect
          error={formik.touched.billed_to && formik.errors.billed_to ? String(formik.errors.billed_to) : ""}
          isClearable
          value={
            formik.values.billed_to
              ? { ...formik.values.billed_to, value: formik.values.billed_to.id, label: formik.values.billed_to.name }
              : null
          }
          label={tr("Billed To")}
          onChange={(e) => formik.setValues((v) => ({ ...v, billed_to: e }))}
        />
      </div>
  
      {/* Default Tax */}
      <div>
        <TaxSelectSettings
          label={tr("Default Tax")}
          value={formik.values?.default_tax || (formik.values as any)?.default_tax_group}
          onChange={(e: any) => {
            if (!e) {
              formik.setFieldValue("default_tax", null);
              formik.setFieldValue("default_tax_group", null);
              return;
            }
            if (e.taxes && Array.isArray(e.taxes)) {
              formik.setFieldValue("default_tax", null);
              formik.setFieldValue("default_tax_group", e.id);
              return;
            }
            formik.setFieldValue("default_tax", e.id ?? e.value);
            formik.setFieldValue("default_tax_group", null);
          }}
          taxOnly={true}
        />
      </div>
  
      {/* Tags */}
      <div>
        <div className="text-[12.8px]">{tr("Tags")}</div>
        <Sort data={tagOptions} sortBy="label" onSorted={setSortedTags} className="mb-1" />
        <Select
          isMulti
          name="tags"
          options={sortedTags}
          className="basic-multi-select"
          label={tr("Tags")}
          classNamePrefix="select"
          value={formik.values.tags}
          onChange={(selectedOptions) => {
            const tags = selectedOptions
              ? selectedOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))
              : [];
            formik.setFieldValue("tags", tags);
          }}
          placeholder={tr("Select tags...")}
        />
      </div>
  
      {/* Map */}
      <div>
        <div className="text-sm">{tr("Map")}</div>
        <NewPropertyMapContainer formik={formik} />
      </div>
  
      {/* Buttons */}
      <div className="flex justify-end space-x-2">
        <OutlineButton
          type="button"
          onClick={() => {
            if (!props.notModal) {
              modal.setModal();
            } else if (props.onCancel) {
              props.onCancel();
            }
          }}
        >
          {tr("Cancel")}
        </OutlineButton>
        <Button primary type="submit" disabled={!formik.isValid || !formik.dirty || mutation.isLoading}>
          {tr("Save")}
        </Button>
      </div>
    </form>
  );
}
